import { all, call, put, takeLatest } from 'redux-saga/effects';
import { Api } from '$api';
import { batchActions } from '$store';
import { mainActions } from '$store/main';
import { toastActions } from '$store/toast';
import { leaderboardActions } from '$store/leaderboard/index';
import { PayloadAction } from '@reduxjs/toolkit';
import router from '$navigation/router';

function* loadWorker(action: PayloadAction<{ main?: boolean } | undefined>) {
  try {
    const params = router.getState().params;
    let leaderboardId = action.payload?.main ? 0 : +params.id || 0;
    let path = '/leaderboard';
    if (leaderboardId > 0) {
      path = `/leaderboard/${leaderboardId}`;
    }

    const data = yield call(Api.get, path);
    yield put(
      batchActions(
        leaderboardActions.setSuccessData({
          id: data.id,
          title: data.title,
          wikiLink: data.wikiLink,
          date: data.date,
          startAt: data.startAt,
          endAt: data.endAt,
          myRank: data.myRank,
          prizes: data.prizes,
          currency: data.currency,
          memberIds: data.memberIds,
          type: data.type,
        }),
        leaderboardActions.setLoadingStatus(''),
        mainActions.setUsers(data.users),
      ),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
    yield put(leaderboardActions.setLoadingStatus('failed'));
  }
}

function* joinWorker(action: PayloadAction<number>) {
  try {
    yield put(toastActions.setToastLoading());
    const data = yield call(Api.post, `/leaderboard/${action.payload}/join`);
    yield put(leaderboardActions.setMyRank(data.myRank));
    yield put(toastActions.setToastSuccess());
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
    yield put(leaderboardActions.setLoadingStatus('failed'));
  }
}

function* loadHistoryWorker() {
  try {
    const data = yield call(Api.get, '/leaderboard/history');
    yield put(
      batchActions(
        leaderboardActions.setHistory(data.items),
        leaderboardActions.setHistoryLoadingStatus(''),
      ),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
    yield put(leaderboardActions.setHistoryLoadingStatus('failed'));
  }
}

function* loadDetailsWorker(action: PayloadAction<number>) {
  try {
    const leaderboardId = action.payload;
    const [leaderboardData, detailsData] = yield all([
      call(Api.get, `/leaderboard/${leaderboardId}`),
      call(Api.get, `/leaderboard/${leaderboardId}/details`),
    ]);

    yield put(
      batchActions(
        leaderboardActions.setSuccessData({
          id: leaderboardData.id,
          title: leaderboardData.title,
          wikiLink: leaderboardData.wikiLink,
          date: leaderboardData.date,
          startAt: leaderboardData.startAt,
          endAt: leaderboardData.endAt,
          myRank: leaderboardData.myRank,
          prizes: leaderboardData.prizes,
          currency: leaderboardData.currency,
          memberIds: leaderboardData.memberIds,
          type: leaderboardData.type,
        }),
        leaderboardActions.setDetails({
          rank: detailsData.rank,
          totalPoints: detailsData.totalPoints,
          paymentPoints: detailsData.paymentPoints,
          newUserPoints: detailsData.newUserPoints,
          transactions: detailsData.transactions,
          newPayingUsers: detailsData.newPayingUsers,
        }),
        leaderboardActions.setLoadingStatus(''),
        leaderboardActions.setDetailsLoadingStatus(''),
        mainActions.setUsers(leaderboardData.users),
        mainActions.setUsers(detailsData.users),
      ),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
    yield put(leaderboardActions.setDetailsLoadingStatus('failed'));
  }
}

export function* leaderboardSaga() {
  yield all([
    takeLatest(leaderboardActions.load, loadWorker),
    takeLatest(leaderboardActions.join, joinWorker),
    takeLatest(leaderboardActions.loadHistory, loadHistoryWorker),
    takeLatest(leaderboardActions.loadDetails, loadDetailsWorker),
  ]);
}
